// Cancel vacation dialog
const handleCancelVacation = () => {
  setVacationDialogOpen(false);
  setEditingVacation(null);
  setVacationStart('');
  setVacationEnd('');
};
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CakeIcon from '@mui/icons-material/Cake';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Chip from '@mui/material/Chip';
import MainCard from 'components/MainCard';
import { fetchEmployees } from 'api/employees';
import { fetchEmployeeAvailability, patchEmployeeAvailability, createEmployeeAvailability } from 'api/employees';
import { patchEmployeeDetails } from 'api/employeeEdit';
import { getEmployeeVacations, createEmployeeVacation, updateEmployeeVacation } from 'api/employeeVacations';
import { useSubStores } from 'contexts/SubStoreContext';

export default function EmployeeDetails() {
  // Vacation state
  const [vacations, setVacations] = useState([]);
  const [vacationsLoading, setVacationsLoading] = useState(true);
  const [vacationDialogOpen, setVacationDialogOpen] = useState(false);
  const [editingVacation, setEditingVacation] = useState(null);
  const [vacationStart, setVacationStart] = useState('');
  const [vacationEnd, setVacationEnd] = useState('');
  const [vacationSaving, setVacationSaving] = useState(false);
  const { employeeId } = useParams();
  const [employmentType, setEmploymentType] = useState('');
  const [workHours, setWorkHours] = useState('');
  const [address, setAddress] = useState('');
  const navigate = useNavigate();
  const { substores } = useSubStores();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedEmployee, setEditedEmployee] = useState(null);
  const [saving, setSaving] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [editAvailDialogOpen, setEditAvailDialogOpen] = useState(false);
  const [editedSchedule, setEditedSchedule] = useState(null);
  const [savingAvail, setSavingAvail] = useState(false);
  const [newSchedule, setNewSchedule] = useState(null);


  // Vacation dialog open for add/edit
  const handleOpenVacationDialog = (vacation = null) => {
    if (vacation) {
      setEditingVacation(vacation);
      setVacationStart(vacation.start);
      setVacationEnd(vacation.end);
    } else {
      setEditingVacation(null);
      setVacationStart('');
      setVacationEnd('');
    }
    setVacationDialogOpen(true);
  };

  // Save vacation (add or edit)
  const handleSaveVacation = async () => {
    setVacationSaving(true);
    try {
      if (editingVacation) {
        // Edit existing
        await updateEmployeeVacation(employeeId, editingVacation.id, {
          start: vacationStart,
          end: vacationEnd
        });
      } else {
        // Add new
        await createEmployeeVacation(employeeId, {
          start: vacationStart,
          end: vacationEnd
        });
      }
      setVacationDialogOpen(false);
      setEditingVacation(null);
      setVacationStart('');
      setVacationEnd('');
      // Refresh vacations
      const data = await getEmployeeVacations(employeeId);
      setVacations(data);
    } catch (err) {
      let msg = 'Failed to save vacation.';
      if (err?.response?.data) {
        if (typeof err.response.data === 'string') msg += '\n' + err.response.data;
        else if (typeof err.response.data === 'object') msg += '\n' + JSON.stringify(err.response.data);
      }
      console.error('Vacation save error:', err);
      alert(msg);
    } finally {
      setVacationSaving(false);
    }
  };

  const handleCancelVacation = () => {
    setVacationDialogOpen(false);
    setEditingVacation(null);
    setVacationStart('');
    setVacationEnd('');
  };

  useEffect(() => {
    const loadEmployee = async () => {
      setLoading(true);
      try {
        const allEmployees = await fetchEmployees();
        const selectedEmployee = allEmployees.find((emp) => String(emp.id) === String(employeeId));
        if (selectedEmployee) {
          setEmployee(selectedEmployee);
        } else {
          navigate('/employees');
        }
      } catch (err) {
        navigate('/employees');
      } finally {
        setLoading(false);
      }
    };
    loadEmployee();
    // Fetch employee availability
    const fetchAvailability = async () => {
      setAvailabilityLoading(true);
      try {
        const data = await fetchEmployeeAvailability(employeeId);
        setAvailability(data);
        // If none, initialize a default schedule for setup cards
        if (!data || !Array.isArray(data) || data.length === 0) {
          const days = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
          const initial = days.reduce((acc, d) => {
            acc[d] = { available: false, start: '', end: '' };
            return acc;
          }, {});
          setNewSchedule(initial);
        } else {
          setNewSchedule(null);
        }
      } catch (err) {
        setAvailability(null);
        const days = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
        const initial = days.reduce((acc, d) => {
          acc[d] = { available: false, start: '', end: '' };
          return acc;
        }, {});
        setNewSchedule(initial);
      } finally {
        setAvailabilityLoading(false);
      }
    };
    fetchAvailability();

    // Fetch employee vacations
    const fetchVacations = async () => {
      setVacationsLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 300));
        const data = await getEmployeeVacations(employeeId);
        setVacations(data);
      } catch (err) {
        setVacations([]);
      } finally {
        setVacationsLoading(false);
      }
    };
    fetchVacations();
    // ...existing code...
    // Vacation dialog open for add/edit
    const handleOpenVacationDialog = (vacation = null) => {
      if (vacation) {
        setEditingVacation(vacation);
        setVacationStart(vacation.start);
        setVacationEnd(vacation.end);
      } else {
        setEditingVacation(null);
        setVacationStart('');
        setVacationEnd('');
      }
      setVacationDialogOpen(true);
    };

    // Save vacation (add or edit)
    const handleSaveVacation = async () => {
      setVacationSaving(true);
      try {
        if (editingVacation) {
          // Edit existing
          await updateEmployeeVacation(employeeId, editingVacation.id, {
            start: vacationStart,
            end: vacationEnd
          });
        } else {
          // Add new
          await createEmployeeVacation(employeeId, {
            start: vacationStart,
            end: vacationEnd
          });
        }
        setVacationDialogOpen(false);
        setEditingVacation(null);
        setVacationStart('');
        setVacationEnd('');
        // Refresh vacations
        const data = await getEmployeeVacations(employeeId);
        setVacations(data);
      } catch (err) {
        alert('Failed to save vacation.');
      } finally {
        setVacationSaving(false);
      }
    };

    const handleCancelVacation = () => {
      setVacationDialogOpen(false);
      setEditingVacation(null);
      setVacationStart('');
      setVacationEnd('');
    };
  }, [employeeId, navigate]);

  const handleBackToEmployees = () => {
    navigate('/employees');
  };

  // Open edit dialog
  const handleEditEmployee = () => {
    setEditedEmployee({
      fullName: employee.fullName || '',
      email: employee.email || '',
      phone: employee.phone || '',
      employment_type: employee.employment_type || '',
      work_hours: employee.work_hours || '',
      address: employee.address || ''
      // Add more fields as needed
    });
    setEditDialogOpen(true);
  };

  // Save changes via PATCH
  const handleSaveEmployee = async () => {
    if (!editedEmployee) return;
    setSaving(true);
    try {
      const patchData = {
        full_name: editedEmployee.fullName,
        email: editedEmployee.email,
        phone: editedEmployee.phone,
        employment_type: editedEmployee.employment_type,
        work_hours: editedEmployee.work_hours,
        address: editedEmployee.address
        // Add more fields as needed
      };
      const updated = await patchEmployeeDetails(employee.id, patchData);
      setEmployee((prev) => ({ ...prev, ...updated }));
      setEditDialogOpen(false);
      setEditedEmployee(null);
    } catch (err) {
      alert('Failed to update employee details.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditedEmployee(null);
  };

  // Helper function to render availability cards
  const renderAvailabilityCards = () => {
    if (!availability || !Array.isArray(availability) || availability.length === 0) {
      return <Typography color="error">No availability data found.</Typography>;
    }

    // Get the first availability record's schedule (employees typically have one availability record)
    const scheduleData = availability[0]?.schedule;
    if (!scheduleData) {
      return <Typography color="error">No schedule data found.</Typography>;
    }

    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    return (
      <Grid container spacing={2}>
        {daysOfWeek.map((day) => {
          const dayData = scheduleData[day];
          const isAvailable = dayData?.available || false;

          return (
            <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={day}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  border: isAvailable ? '2px solid #4caf50' : '2px solid #f44336',
                  backgroundColor: isAvailable ? 'rgba(76, 175, 80, 0.05)' : 'rgba(244, 67, 54, 0.05)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  }
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, px: 2, py: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mr: 2 }}>
                      {day}
                    </Typography>
                    <Chip
                      icon={isAvailable ? <CheckCircleIcon /> : <CancelIcon />}
                      label={isAvailable ? 'Available' : 'Not Available'}
                      color={isAvailable ? 'success' : 'error'}
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  {isAvailable && dayData ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                      <AccessTimeIcon color="primary" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        {dayData.start} - {dayData.end}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                      <CancelIcon color="error" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        Off Day
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  if (loading) {
    return (
      <MainCard title="Employee Details">
        <Typography>Loading...</Typography>
      </MainCard>
    );
  }
  if (!employee) {
    return (
      <MainCard title="Employee Details">
        <Typography>Employee not found.</Typography>
      </MainCard>
    );
  }

  // Get the substore name for this employee
  // Try to get substore name from substores context, fallback to employee.substore?.name
  let substoreName = 'Unknown Store';
  if (employee.substore && employee.substore.name) {
    substoreName = employee.substore.name;
  } else if (substores && Array.isArray(substores)) {
    const found = substores.find((s) => s.id === employee.substoreId);
    if (found) substoreName = found.storeName;
  }

  return (
    <MainCard title="Employee Details">
      <Box mb={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBackToEmployees} sx={{ mb: 2 }}>
          Back to Employees
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={handleEditEmployee} sx={{ mb: 2 }}>
            Edit
          </Button>
        </Box>
      </Box>

      <Card variant="outlined">
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h3" gutterBottom color="primary" sx={{ mb: 3, fontWeight: 600 }}>
            {employee.fullName}
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Grid container spacing={4}>
            <Grid item size={{ xs: 12, md: 6 }}>
              <Box>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                  Personal Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PhoneIcon color="primary" />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Phone Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                        {employee.phone || 'Not specified'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PersonIcon color="primary" />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Email
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                        {employee.email || 'Not specified'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocationOnIcon color="primary" />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Address
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                        {employee.address || 'Not specified'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                  Work Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocationOnIcon color="primary" />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Assigned Store
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                        {substoreName}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocationOnIcon color="primary" />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Employment Type
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                        {employee.employment_type || 'Not specified'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocationOnIcon color="primary" />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        Work Hours
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                        {employee.work_hours || 'Not specified'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Box sx={{ mt: 2 }}>
            {/* Employee Vacations Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Vacations
                </Typography>
                <Button variant="contained" color="primary" onClick={() => handleOpenVacationDialog()}>
                  Add Vacation
                </Button>
              </Box>
              {vacationsLoading ? (
                <Typography>Loading vacations...</Typography>
              ) : vacations.length === 0 ? (
                <Typography color="text.secondary">No vacations found.</Typography>
              ) : (
                <Grid container spacing={2}>
                  {vacations.map((vac) => (
                    <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={vac.id}>
                      <Card variant="outlined" sx={{ p: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Vacation
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Start: {vac.start_date}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            End: {vac.end_date}
                          </Typography>
                        </CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                          <Button
                            size="small"
                            onClick={() =>
                              handleOpenVacationDialog({
                                ...vac,
                                start: vac.start_date,
                                end: vac.end_date
                              })
                            }
                          >
                            Edit
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
            {/* Vacation Dialog */}
            <Dialog open={vacationDialogOpen} onClose={handleCancelVacation} maxWidth="xs" fullWidth>
              <DialogTitle>{editingVacation ? 'Edit Vacation' : 'Add Vacation'}</DialogTitle>
              <DialogContent>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={vacationStart}
                    onChange={(e) => setVacationStart(e.target.value)}
                    fullWidth
                    slotProps={{inputLabel: {shrink: true}}}
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    value={vacationEnd}
                    onChange={(e) => setVacationEnd(e.target.value)}
                    fullWidth
                    slotProps={{inputLabel: {shrink: true}}}                  
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCancelVacation}>Cancel</Button>
                <Button onClick={handleSaveVacation} variant="contained" disabled={vacationSaving || !vacationStart || !vacationEnd}>
                  Save
                </Button>
              </DialogActions>
            </Dialog>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                Weekly Availability
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  // Prepare editable schedule from current availability; if none, initialize defaults
                  let schedule = {};
                  if (availability && Array.isArray(availability) && availability[0]?.schedule) {
                    schedule = { ...availability[0].schedule };
                  } else {
                    const days = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
                    schedule = days.reduce((acc, d) => {
                      acc[d] = { available: false, start: '', end: '' };
                      return acc;
                    }, {});
                  }
                  setEditedSchedule(schedule);
                  setEditAvailDialogOpen(true);
                }}
                sx={{ mb: 2 }}
              >
                Edit Availability
            </Button>
            </Box>
            {availabilityLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
                <AccessTimeIcon color="primary" />
                <Typography>Loading availability...</Typography>
              </Box>
            ) : availability ? (
              renderAvailabilityCards()
            ) : (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    No availability set yet. Configure weekly availability below.
                  </Typography>
                </Box>
                {newSchedule && (
                  <>
                    <Grid container spacing={2}>
                      {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((day) => {
                        const dayData = newSchedule[day] || { available: false, start: '', end: '' };
                        const isAvailable = !!dayData.available;
                        return (
                          <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={day}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="h6" sx={{ mb: 2 }}>{day}</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <Chip
                                  icon={isAvailable ? <CheckCircleIcon /> : <CancelIcon />}
                                  label={isAvailable ? 'Available' : 'Not Available'}
                                  color={isAvailable ? 'success' : 'error'}
                                  size="small"
                                  variant="outlined"
                                />
                                <Button
                                  size="small"
                                  onClick={() => {
                                    setNewSchedule((prev) => {
                                      const newAvailable = !prev[day]?.available;
                                      return {
                                        ...prev,
                                        [day]: {
                                          ...prev[day],
                                          available: newAvailable,
                                          start: newAvailable ? prev[day]?.start || '' : '',
                                          end: newAvailable ? prev[day]?.end || '' : ''
                                        }
                                      };
                                    });
                                  }}
                                >
                                  Toggle
                                </Button>
                              </Box>
                              {isAvailable && (
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                  <TextField
                                    label="Start Time"
                                    type="time"
                                    value={dayData.start || ''}
                                    onChange={(e) => setNewSchedule((prev) => ({ ...prev, [day]: { ...prev[day], start: e.target.value } }))}
                                    slotProps={{inputLabel: {shrink: true}}}
                                    size="small"
                                  />
                                  <TextField
                                    label="End Time"
                                    type="time"
                                    value={dayData.end || ''}
                                    onChange={(e) => setNewSchedule((prev) => ({ ...prev, [day]: { ...prev[day], end: e.target.value } }))}
                                    IslotProps={{inputLabel: {shrink: true}}}
                                    size="small"
                                  />
                                </Box>
                              )}
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <Button
                        variant="contained"
                        disabled={savingAvail}
                        onClick={async () => {
                          if (!newSchedule) return;
                          setSavingAvail(true);
                          try {
                            await createEmployeeAvailability(employeeId, newSchedule);
                            // Reload availability
                            const data = await fetchEmployeeAvailability(employeeId);
                            setAvailability(data);
                            setNewSchedule(null);
                          } catch (err) {
                            alert('Failed to create availability.');
                          } finally {
                            setSavingAvail(false);
                          }
                        }}
                      >
                        Save Availability
                      </Button>
                    </Box>
                  </>
                )}
              </>
            )}
          </Box>

          {/* Edit Availability Dialog */}
          <Dialog open={editAvailDialogOpen} onClose={() => setEditAvailDialogOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Edit Weekly Availability</DialogTitle>
            <DialogContent>
              {editedSchedule && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {Object.keys(editedSchedule).length === 0 ? (
                    <Typography color="error">No schedule data to edit.</Typography>
                  ) : (
                    ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => {
                      const dayData = editedSchedule[day];
                      return (
                        <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={day}>
                          <Card variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                              {day}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                              <Chip
                                icon={dayData?.available ? <CheckCircleIcon /> : <CancelIcon />}
                                label={dayData?.available ? 'Available' : 'Not Available'}
                                color={dayData?.available ? 'success' : 'error'}
                                size="small"
                                variant="outlined"
                              />
                              <Button
                                size="small"
                                onClick={() => {
                                  setEditedSchedule((prev) => {
                                    const newAvailable = !prev[day]?.available;
                                    return {
                                      ...prev,
                                      [day]: {
                                        ...prev[day],
                                        available: newAvailable,
                                        start: newAvailable ? prev[day]?.start || '' : '',
                                        end: newAvailable ? prev[day]?.end || '' : ''
                                      }
                                    };
                                  });
                                }}
                              >
                                Toggle
                              </Button>
                            </Box>
                            {dayData?.available && (
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                  label="Start Time"
                                  type="time"
                                  value={dayData.start || ''}
                                  onChange={(e) =>
                                    setEditedSchedule((prev) => ({ ...prev, [day]: { ...prev[day], start: e.target.value } }))
                                  }
                                  InputLabelProps={{ shrink: true }}
                                  size="small"
                                />
                                <TextField
                                  label="End Time"
                                  type="time"
                                  value={dayData.end || ''}
                                  onChange={(e) => setEditedSchedule((prev) => ({ ...prev, [day]: { ...prev[day], end: e.target.value } }))}
                                  InputLabelProps={{ shrink: true }}
                                  size="small"
                                />
                              </Box>
                            )}
                          </Card>
                        </Grid>
                      );
                    })
                  )}
                </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditAvailDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                disabled={savingAvail}
                onClick={async () => {
                  if (!editedSchedule) return;
                  setSavingAvail(true);
                  try {
                    if (availability && Array.isArray(availability) && availability[0]?.id) {
                      await patchEmployeeAvailability(employeeId, availability[0].id, editedSchedule);
                    } else {
                      
                      await createEmployeeAvailability(employeeId, editedSchedule);
                    }
                    setEditAvailDialogOpen(false);
                    // Refresh availability after save
                    const data = await fetchEmployeeAvailability(employeeId);
                    setAvailability(data);
                  } catch (err) {
                    alert('Failed to update availability.');
                  } finally {
                    setSavingAvail(false);
                  }
                }}
              >
                Save Changes
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>

      {/* Edit Employee Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCancelEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Employee Details</DialogTitle>
        <DialogContent>
          {editedEmployee && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Full Name"
                value={editedEmployee.fullName}
                onChange={(e) => setEditedEmployee((prev) => ({ ...prev, fullName: e.target.value }))}
                fullWidth
                sx={{ mb: 2 }}
              />
              <TextField
                label="Email"
                value={editedEmployee.email}
                onChange={(e) => setEditedEmployee((prev) => ({ ...prev, email: e.target.value }))}
                fullWidth
                sx={{ mb: 2 }}
              />
              <TextField
                label="Phone Number"
                value={editedEmployee.phone}
                onChange={(e) => setEditedEmployee((prev) => ({ ...prev, phone: e.target.value }))}
                fullWidth
                sx={{ mb: 2 }}
              />
              <TextField
                label="Employment Type"
                value={editedEmployee.employment_type}
                onChange={(e) => setEditedEmployee((prev) => ({ ...prev, employment_type: e.target.value }))}
                fullWidth
                sx={{ mb: 2 }}
              />
              <TextField
                label="Work Hours"
                value={editedEmployee.work_hours}
                onChange={(e) => setEditedEmployee((prev) => ({ ...prev, work_hours: e.target.value }))}
                fullWidth
                sx={{ mb: 2 }}
              />
              <TextField
                label="Address"
                value={editedEmployee.address}
                onChange={(e) => setEditedEmployee((prev) => ({ ...prev, address: e.target.value }))}
                fullWidth
                sx={{ mb: 2 }}
              />
              {/* Add more editable fields as needed */}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Cancel</Button>
          <Button onClick={handleSaveEmployee} variant="contained" disabled={saving}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </MainCard>
  );
}
